import type { JobFacets } from '@/lib/jobs/queries'
import type { JobFilters } from '@/lib/jobs/filters'
import type { JobCard as JobCardData, PageWithLayout, Paginated } from '@/types'

import { useState } from 'react'
import { useRouter } from 'next/router'
import { BellPlus, SearchX, SlidersHorizontal, X } from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, errorMessage } from '@/lib/api-client'
import { withPage } from '@/lib/server/page'
import { formatNumber } from '@/lib/format'
import { searchJobs, getJobFacets } from '@/lib/jobs/queries'
import { ensureFreshJobs } from '@/lib/ingest/freshness'
import { scoreJobCards, searchJobsByMatch } from '@/lib/match/score-cards'

import {
    DEFAULT_FILTERS,
    activeFilterCount,
    describeFilters,
    filtersToHref,
    parseFilters
} from '@/lib/jobs/filters'

import { Seo } from '@/components/generals/seo'
import { publicLayout } from '@/components/generals/page-layouts'
import { useSite } from '@/components/generals/site-context'
import { EmptyState } from '@/components/generals/states'
import { useRouteLoading } from '@/components/generals/route-progress'
import { SearchBar } from '@/components/jobs/search-bar'
import { JobFiltersPanel } from '@/components/jobs/job-filters'
import { JobCard, JobCardSkeleton } from '@/components/jobs/job-card'
import { Pagination } from '@/components/jobs/pagination'
import { CollectingState } from '@/components/jobs/collecting-state'
import { Button } from '@/components/ui/button'
import { SimpleSelect } from '@/components/ui/select'

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

import {
    JOB_SORTS,
    categoryLabel,
    levelLabel,
    workTypeLabel
} from '@/constants/taxonomy'

interface JobsProps {
    filters: JobFilters
    results: Paginated<JobCardData>
    facets: JobFacets
    collecting: boolean
    canMatch: boolean
}

export const getServerSideProps = withPage<JobsProps>(async (context, { viewer }) => {
    const filters = parseFilters(context.query as Record<string, string | Array<string>>)
    const collection = await ensureFreshJobs().catch(error => {
        console.error('Could not check job freshness', error)
        return { collecting: false, lastFinishedAt: null }
    })

    const canMatch = Boolean(viewer?.primaryResumeId)
    const byMatch = filters.sort === 'match' && viewer?.primaryResumeId

    const [results, facets] = await Promise.all([
        byMatch && viewer?.primaryResumeId
            ? searchJobsByMatch(filters, viewer.id, viewer.primaryResumeId)
            : searchJobs(filters, viewer?.id ?? null),
        getJobFacets(filters)
    ])

    if (!byMatch && viewer?.primaryResumeId) {
        results.items = await scoreJobCards(results.items, viewer.id, viewer.primaryResumeId)
    }

    return {
        props: {
            filters,
            results,
            facets,
            canMatch,
            collecting: collection.collecting && results.total === 0 && !filters.q && activeFilterCount(filters) === 0
        }
    }
})

const JobsPage: PageWithLayout<JobsProps> = ({ filters, results, facets, collecting, canMatch }) => {
    const router = useRouter()
    const { viewer } = useSite()
    const loading = useRouteLoading()
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [creatingAlert, setCreatingAlert] = useState(false)

    const apply = (next: Partial<JobFilters>) => {
        void router.push(filtersToHref({ ...filters, ...next, page: 1 }), undefined, { scroll: false })
    }

    const clearAll = () => apply({ ...DEFAULT_FILTERS, q: filters.q, location: '' })

    const createAlert = async () => {
        if (!viewer) {
            toast.info('Sign in to get daily emails when new jobs match this search.')
            await router.push(`/login?next=${encodeURIComponent(router.asPath)}`)
            return
        }

        setCreatingAlert(true)
        try {
            await apiFetch('/api/alerts', {
                body: {
                    name: describeFilters(filters),
                    filters: { ...filters, page: 1 }
                }
            })
            toast.success('Alert created. New matches arrive in a daily email digest.', {
                action: { label: 'Manage', onClick: () => void router.push('/settings?tab=alerts') }
            })
        }
        catch (error) {
            toast.error(errorMessage(error))
        }
        finally {
            setCreatingAlert(false)
        }
    }

    const chips: Array<{ key: string, label: string, remove: Partial<JobFilters> }> = [
        ...(filters.location ? [{ key: 'location', label: filters.location, remove: { location: '' } }] : []),
        ...(filters.skill ? [{ key: 'skill', label: filters.skill, remove: { skill: '' } }] : []),
        ...filters.workTypes.map(value => ({ key: `work-${value}`, label: workTypeLabel(value) ?? value, remove: { workTypes: filters.workTypes.filter(item => item !== value) } })),
        ...filters.levels.map(value => ({ key: `level-${value}`, label: levelLabel(value) ?? value, remove: { levels: filters.levels.filter(item => item !== value) } })),
        ...filters.categories.map(value => ({ key: `category-${value}`, label: categoryLabel(value), remove: { categories: filters.categories.filter(item => item !== value) } })),
        ...filters.companies.map(value => ({ key: `company-${value}`, label: facets.companies.find(item => item.value === value)?.label ?? value, remove: { companies: filters.companies.filter(item => item !== value) } })),
        ...(filters.salaryMin ? [{ key: 'salary', label: `$${Math.round(filters.salaryMin / 1000)}k+`, remove: { salaryMin: null } }] : []),
        ...(filters.postedWithin ? [{ key: 'posted', label: `Last ${filters.postedWithin} ${filters.postedWithin === 1 ? 'day' : 'days'}`, remove: { postedWithin: null } }] : [])
    ]

    const sortOptions = JOB_SORTS.filter(option => {
        if (option.value === 'match') return canMatch
        if (option.value === 'relevant') return Boolean(filters.q)
        return true
    })

    const count = activeFilterCount(filters)
    const title = filters.q ? `${filters.q} jobs` : filters.skill ? `${filters.skill} jobs` : 'Explore tech jobs'

    const panel = (
        <JobFiltersPanel
            filters={filters}
            facets={facets}
            onChange={next => {
                apply(next)
                setFiltersOpen(false)
            }}
            onClear={() => {
                clearAll()
                setFiltersOpen(false)
            }}
        />
    )

    return (
        <>
            <Seo
                title={title}
                path={filtersToHref({ ...filters, page: results.page })}
                description={`Browse ${formatNumber(results.total)} technology jobs collected from company career pages. Filter by remote work, location, experience and salary.`}
            />

            <section className='border-b border-border/70 bg-background'>
                <div className='container-page flex flex-col gap-4 py-8'>
                    <div className='flex flex-col gap-1'>
                        <h1 className='text-2xl font-bold sm:text-3xl'>{title}</h1>
                        <p className='text-sm text-muted-foreground'>Fresh openings from company career pages, updated every day.</p>
                    </div>
                    <SearchBar
                        defaultQuery={filters.q}
                        defaultLocation={filters.location}
                        onSearch={(q, location) => apply({ q, location, sort: q ? 'relevant' : 'recent' })}
                    />
                </div>
            </section>

            <div className='container-page grid gap-8 py-8 lg:grid-cols-[17rem_1fr]'>
                <aside aria-label='Job filters' className='hidden lg:block'>
                    <div className='sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto rounded-2xl border border-border/80 bg-card p-5 shadow-card'>
                        {panel}
                    </div>
                </aside>

                <section aria-labelledby='results-heading' className='flex min-w-0 flex-col gap-4'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <h2 id='results-heading' className='text-sm text-muted-foreground'>
                            {collecting ? 'Collecting jobs' : (
                                <>
                                    <span className='font-semibold text-foreground'>{formatNumber(results.total)}</span>
                                    {' '}{results.total === 1 ? 'job' : 'jobs'}
                                    {results.pageCount > 1 && ` · page ${results.page} of ${results.pageCount}`}
                                </>
                            )}
                        </h2>

                        <div className='flex items-center gap-2'>
                            <Button variant='outline' className='lg:hidden' onClick={() => setFiltersOpen(true)}>
                                <SlidersHorizontal />
                                Filters{count > 0 ? ` (${count})` : ''}
                            </Button>
                            <Button variant='outline' onClick={() => void createAlert()} disabled={creatingAlert}>
                                <BellPlus />
                                <span className='hidden sm:inline'>Create alert</span>
                            </Button>
                            <SimpleSelect
                                aria-label='Sort jobs'
                                className='w-44'
                                value={filters.sort}
                                onValueChange={value => apply({ sort: value as JobFilters['sort'] })}
                                options={sortOptions}
                            />
                        </div>
                    </div>

                    {chips.length > 0 && (
                        <ul className='flex flex-wrap items-center gap-2' aria-label='Active filters'>
                            {chips.map(chip => (
                                <li key={chip.key}>
                                    <button
                                        type='button'
                                        onClick={() => apply(chip.remove)}
                                        className='inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium hover:border-primary/40 hover:text-primary'
                                    >
                                        {chip.label}
                                        <X className='size-3' />
                                        <span className='sr-only'>Remove filter</span>
                                    </button>
                                </li>
                            ))}
                            <li>
                                <button type='button' onClick={clearAll} className='text-xs font-semibold text-primary hover:underline'>
                                    Clear all
                                </button>
                            </li>
                        </ul>
                    )}

                    {collecting ? (
                        <CollectingState />
                    ) : loading ? (
                        <div className='flex flex-col gap-3' aria-busy='true'>
                            {Array.from({ length: 6 }, (_, index) => <JobCardSkeleton key={index} />)}
                        </div>
                    ) : results.items.length === 0 ? (
                        <EmptyState
                            icon={SearchX}
                            title='No jobs match these filters'
                            description='Try a broader keyword, remove a filter or two, or create an alert and we will email you when a match is posted.'
                            action={(
                                <>
                                    <Button onClick={clearAll}>Clear filters</Button>
                                    <Button variant='outline' onClick={() => void createAlert()}>
                                        <BellPlus />
                                        Alert me
                                    </Button>
                                </>
                            )}
                        />
                    ) : (
                        <div className='flex flex-col gap-3'>
                            {results.items.map(job => <JobCard key={job.id} job={job} />)}
                        </div>
                    )}

                    {!collecting && !loading && (
                        <div className='pt-4'>
                            <Pagination
                                page={results.page}
                                pageCount={results.pageCount}
                                hrefFor={page => filtersToHref({ ...filters, page })}
                            />
                        </div>
                    )}
                </section>
            </div>

            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetContent side='bottom'>
                    <SheetHeader className='sr-only'>
                        <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className='p-5 pt-6'>
                        {panel}
                    </div>
                </SheetContent>
            </Sheet>
        </>
    )
}

JobsPage.getLayout = publicLayout

export default JobsPage
